import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, TextField, Typography, Paper, CircularProgress, Chip, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REPORT_URL = `${BACKEND_URL}/api/report/translation-usage`;

// Where each translation was asked for (translation_usage_logs.source).
const SOURCE_LABEL = {
  'mercari-title': 'ชื่อสินค้าในรายการ (หน้าเว็บ)',
  'mercari-description': 'คำอธิบายสินค้า (หน้าสินค้า)',
  search_results: 'ผลค้นหา',
  image_search_results: 'ค้นหาด้วยรูป',
  single_item_detail: 'หน้าสินค้า Mercari',
  shop_item_detail: 'หน้าสินค้า Mercari Shops',
  related_items: 'สินค้าที่เกี่ยวข้อง',
  general_recommendations: 'สินค้าแนะนำ',
  view_history: 'สินค้าที่เคยดู',
  seller_profile: 'หน้าผู้ขาย',
  shop_seller_profile: 'หน้าร้าน Mercari Shops',
  'paypay-title': 'ชื่อสินค้า PayPay (หน้าสินค้า / checkout / รายการโปรด)',
  'paypay-search': 'คำค้นหา PayPay → ญี่ปุ่น',
  unknown: 'ไม่ระบุ (รวมคำค้นหา Mercari → ญี่ปุ่น)',
};

const METHOD = {
  free: { label: 'ฟรี', color: 'success' },
  paid: { label: 'เสียเงิน', color: 'error' },
  cache: { label: 'จากคลัง', color: 'default' },
  blocked: { label: 'ถูกบล็อก', color: 'warning' },
};

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return toYmd(d); };

const num = (n) => Number(n || 0).toLocaleString();
const usd = (n) => `$${Number(n || 0).toFixed(4)}`;
const time = (iso) => (iso ? new Date(iso).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' }) : '—');
const pct = (rate) => (rate === null || rate === undefined ? '—' : `${(rate * 100).toFixed(1)}%`);

function StatCard({ label, value, sub, color = 'inherit' }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', minWidth: 150 }}>
      <Typography variant="h4" fontWeight="bold" color={color}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {sub && <Typography variant="caption" display="block" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

/**
 * Translation usage (free / paid / cache) per source, language and day, plus why
 * the free in-browser translation failed. Replaces the per-fallback LINE alert.
 */
const TranslationUsageReport = () => {
  const [range, setRange] = useState({ from: firstOfMonth(), to: toYmd(new Date()) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(REPORT_URL, { params: range });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดรายงานไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const totals = data?.totals;

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, p: 2 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>การใช้งานระบบแปลภาษา</Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <TextField label="ตั้งแต่" type="date" size="small" value={range.from}
          onChange={(e) => setRange({ ...range, from: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField label="ถึง" type="date" size="small" value={range.to}
          onChange={(e) => setRange({ ...range, to: e.target.value })} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" onClick={load} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>
          ดูรายงาน
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {totals && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <StatCard label="แปลฟรี (% ของรายการที่ต้องแปล)" value={pct(data.freeRate)} color="success.main" />
            <StatCard label="รายการแปลฟรี" value={num(totals.free.items)} sub={`${num(totals.free.chars)} ตัวอักษร`} />
            <StatCard label="แปลฟรีสำเร็จเพราะลองซ้ำ" value={num(totals.free.retried)}
              sub="ไม่ต้องจ่ายเงินเพราะเบราว์เซอร์ลองแปลฟรีอีกครั้ง" color="success.main" />
            <StatCard label="รายการแปลเสียเงิน" value={num(totals.paid.items)} sub={`${num(totals.paid.chars)} ตัวอักษร`} color="error.main" />
            <StatCard label="ตอบจากคลัง (ไม่ต้องแปล)" value={num(totals.cache.items)} />
            <StatCard label="ถูกบล็อก (ปิดแปลเสียเงินอยู่)" value={num(totals.blocked.items)}
              sub={`${num(totals.blocked.events)} คำขอ`} color="warning.main" />
            <StatCard label="ค่าใช้จ่ายโดยประมาณ" value={usd(totals.paid.costUsd)} sub="Google $20 / 1 ล้านตัวอักษร" color="error.main" />
          </Box>

          <Typography variant="h6" mb={1}>แยกตามที่มา</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ที่มา</TableCell>
                  <TableCell>วิธี</TableCell>
                  <TableCell>ภาษา</TableCell>
                  <TableCell align="right">ครั้ง</TableCell>
                  <TableCell align="right">รายการ</TableCell>
                  <TableCell align="right">ตัวอักษร</TableCell>
                  <TableCell align="right">ค่าใช้จ่าย</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.bySource.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center">ไม่มีข้อมูลในช่วงนี้</TableCell></TableRow>
                )}
                {data.bySource.map((row) => (
                  <TableRow key={`${row.source}-${row.method}-${row.language}`}>
                    <TableCell>
                      {SOURCE_LABEL[row.source] || row.source}
                      <Typography variant="caption" display="block" color="text.secondary">{row.source}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={METHOD[row.method]?.label || row.method} color={METHOD[row.method]?.color || 'default'} />
                    </TableCell>
                    <TableCell>{row.language || '—'}</TableCell>
                    <TableCell align="right">{num(row.events)}</TableCell>
                    <TableCell align="right">{num(row.items)}</TableCell>
                    <TableCell align="right">{num(row.chars)}</TableCell>
                    <TableCell align="right">{row.method === 'paid' ? usd(row.costUsd) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 480px' }}>
              <Typography variant="h6" mb={1}>รายวัน (จำนวนรายการ)</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>วันที่</TableCell>
                      <TableCell align="right">ฟรี</TableCell>
                      <TableCell align="right">เสียเงิน</TableCell>
                      <TableCell align="right">จากคลัง</TableCell>
                      <TableCell align="right">ถูกบล็อก</TableCell>
                      <TableCell align="right">% ฟรี</TableCell>
                      <TableCell align="right">ค่าใช้จ่าย</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.daily.map((d) => (
                      <TableRow key={d.date}>
                        <TableCell>{d.date}</TableCell>
                        <TableCell align="right">{num(d.free)}</TableCell>
                        <TableCell align="right">{num(d.paid)}</TableCell>
                        <TableCell align="right">{num(d.cache)}</TableCell>
                        <TableCell align="right">{num(d.blocked)}</TableCell>
                        <TableCell align="right">{pct(d.free + d.paid ? d.free / (d.free + d.paid) : null)}</TableCell>
                        <TableCell align="right">{usd(d.costUsd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={{ flex: '1 1 360px' }}>
              <Typography variant="h6" mb={1}>สาเหตุที่แปลฟรีไม่สำเร็จ</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>สาเหตุ</TableCell>
                      <TableCell align="right">ครั้ง</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.topReasons.length === 0 && (
                      <TableRow><TableCell colSpan={2} align="center">ไม่มี</TableCell></TableRow>
                    )}
                    {data.topReasons.map((r) => (
                      <TableRow key={r.reason}>
                        <TableCell sx={{ wordBreak: 'break-word' }}>{r.reason}</TableCell>
                        <TableCell align="right">{num(r.count)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          <Typography variant="h6" mt={3} mb={1}>ใครเรียกแปลแบบเสียเงิน / ถูกบล็อกมากที่สุด (ฝั่งลูกค้า)</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            ถ้าคนเดียว (IP / เบราว์เซอร์เดียว) มีคำขอเยอะผิดปกติ อาจเป็นบอท — เบราว์เซอร์ที่ไม่ใช่ Mozilla/… (เช่น python, curl, HeadlessChrome) แปลว่าเรียก API ตรงไม่ผ่านหน้าเว็บ
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ผู้ใช้</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>เบราว์เซอร์</TableCell>
                  <TableCell align="right">คำขอ</TableCell>
                  <TableCell align="right">เสียเงิน</TableCell>
                  <TableCell align="right">ถูกบล็อก</TableCell>
                  <TableCell align="right">ค่าใช้จ่าย</TableCell>
                  <TableCell>ช่วงเวลา</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.topRequesters.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center">ไม่มี</TableCell></TableRow>
                )}
                {data.topRequesters.map((r) => (
                  <TableRow key={`${r.userId}-${r.ip}-${r.userAgent}`}>
                    <TableCell>{r.userId ? `#${r.userId}` : 'ไม่ได้ล็อกอิน'}</TableCell>
                    <TableCell>{r.ip || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>{r.userAgent || '—'}</TableCell>
                    <TableCell align="right">{num(r.events)}</TableCell>
                    <TableCell align="right">{num(r.paid)}</TableCell>
                    <TableCell align="right">{num(r.blocked)}</TableCell>
                    <TableCell align="right">{usd(r.costUsd)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{time(r.firstAt)}<br />{time(r.lastAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" mt={3} mb={1}>บอท / สคริปต์ (รวมหน้าสินค้าที่เซิร์ฟเวอร์แปลเอง)</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            บอทที่เข้าหน้าสินค้าทำให้เซิร์ฟเวอร์ต้องแปลชื่อสินค้าแบบเสียเงินทุกชิ้นที่ยังไม่เคยแปล — ถ้าค่าใช้จ่ายสูง ให้บล็อกบอทนั้นที่ Vercel Firewall
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>บอท</TableCell>
                  <TableCell align="right">คำขอ</TableCell>
                  <TableCell align="right">เสียเงิน</TableCell>
                  <TableCell align="right">ถูกบล็อก</TableCell>
                  <TableCell align="right">IP</TableCell>
                  <TableCell align="right">ค่าใช้จ่าย</TableCell>
                  <TableCell>ช่วงเวลา</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* `bots` is missing until the backend that returns it is deployed. */}
                {!data.bots?.length && (
                  <TableRow><TableCell colSpan={7} align="center">ไม่มี</TableCell></TableRow>
                )}
                {(data.bots || []).map((b) => (
                  <TableRow key={b.bot}>
                    <TableCell>{b.bot}</TableCell>
                    <TableCell align="right">{num(b.events)}</TableCell>
                    <TableCell align="right">{num(b.paid)}</TableCell>
                    <TableCell align="right">{num(b.blocked)}</TableCell>
                    <TableCell align="right">{num(b.ips)}</TableCell>
                    <TableCell align="right">{usd(b.costUsd)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{time(b.firstAt)}<br />{time(b.lastAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default TranslationUsageReport;
